import { useMemo, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterGroupingEnum,
  FormTemplate,
  FormTemplateFilterInputObject,
  useFormCategoriesSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import { getFormCategoriesLoadOptions as getFormCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/formCategoriesSearchList';
import useFormTemplatesQueryHook from 'utils/fetch/hooks/useFormTemplatesQueryHook';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import getFormTemplatesColumns from './utils/getFormTemplatesColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';

// FormTemplatesModalTableMultiselect intentionally does NOT expose a `mode`
// prop. Unlike Assets/SiteAreas/Documents/Tasks, the FormTemplate GraphQL
// schema has no `id`-IN filter — the Selected tab must fetch via the
// `documentationsId` backward link or fall back to filtering the All-tab
// cache. SelectedOnly mode can't display the user's selections in create
// flow (no documentationId, no All-tab data), so the WithTabs contract is
// the only one we can support honestly.

type FormTemplatesModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  formRef: FormRefType;
  fieldName?: string;
  // When editing a documentation, the Selected tab fetches templates linked
  // to this documentation via the `documentationsId` filter. Without an id
  // (create flow), the Selected tab falls back to the All-tab cache.
  documentationId?: string;
};

function FormTemplatesModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = DocumentFormFieldNames.FormTemplates,
  documentationId,
}: FormTemplatesModalTableMultiselectProps) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedFormTemplates = useSelectedFormFieldIds(formRef, fieldName);

  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.FormCategories]: [],
    }),
    [],
  );

  const {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode: ModalTableMultiselectMode.WithTabs,
    selectedIds: selectedFormTemplates,
    filterStateDefault,
  });

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<FormTemplate>({
      isOpen,
      mode: ModalTableMultiselectMode.WithTabs,
      activeTab,
      isSelectedOnlyMode: false,
    });

  const additionalFilters = useMemo(() => {
    if (!filterState) return undefined;
    return createAdditionalFilters(filterState[0] || {});
  }, [filterState]);

  // All tab query
  const {
    formTemplates: collection,
    formTemplatesLoading,
    formTemplatesFirstLoading,
    totalFormTemplatesCount: totalCount,
    fetchFormTemplateIds: fetchIds,
  } = useFormTemplatesQueryHook({
    searchQuery: searchState?.[0] || '',
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    additionalFilters,
    skip: !shouldRunAllQuery,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: formTemplatesLoading,
      firstLoading: formTemplatesFirstLoading,
    });
  }, [
    collection,
    formTemplatesFirstLoading,
    formTemplatesLoading,
    handleAllTabQueryResult,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: formTemplatesFirstLoading,
  });

  // Selected-tab additional filters: include the user's filter chips PLUS the
  // documentationsId filter (BE matches templates linked to this doc). The
  // FormTemplate schema has no `id` filter, so the Selected query is skipped
  // entirely when there is no documentationId — we fall back to filtering the
  // All-tab cache by selected IDs for the create flow.
  const additionalFiltersSelected = useMemo(() => {
    const base = createAdditionalFilters(filterStateSelected[0] || {});
    if (documentationId) {
      base.push({
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentationId },
          filterGroup: 'documentationsId',
        },
      } as FormTemplateFilterInputObject);
    }
    return base;
  }, [filterStateSelected, documentationId]);

  const {
    formTemplates: collectionSelectedRaw,
    formTemplatesLoading: formTemplatesLoadingSelected,
    formTemplatesFirstLoading: formTemplatesFirstLoadingSelected,
    totalFormTemplatesCount: totalCountSelectedRaw,
  } = useFormTemplatesQueryHook({
    searchQuery: searchStateSelected[0],
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    additionalFilters: additionalFiltersSelected,
    skip:
      !isOpen ||
      !isSelectedTabActive ||
      !documentationId ||
      idsForSelectedTabQuery.length === 0,
  });

  // In edit mode, the Selected-tab query fetches by documentationsId.
  // In create mode (no documentationId), that query is skipped, so we fall
  // back to filtering the All-tab cache by selected IDs.
  const collectionSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return [];
    if (documentationId) return collectionSelectedRaw;
    const selectedSet = new Set(idsForSelectedTabQuery);
    return allTabCollection.filter((item) => selectedSet.has(String(item.id)));
  }, [
    allTabCollection,
    collectionSelectedRaw,
    documentationId,
    idsForSelectedTabQuery,
  ]);
  const totalCountSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return 0;
    if (documentationId) return totalCountSelectedRaw;
    return collectionSelected.length;
  }, [
    collectionSelected.length,
    documentationId,
    idsForSelectedTabQuery.length,
    totalCountSelectedRaw,
  ]);

  // Form categories filter options
  const [fetchFormCategoriesLazyQuery] = useFormCategoriesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getFormCategoriesLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) =>
      getFormCategoriesLoadOptionsFn(fetchFormCategoriesLazyQuery, filters),
  );

  const columns = getFormTemplatesColumns({ t });
  const filterFormFields = useFilterFields({
    getFormCategoriesLoadOptions,
  });

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<FormTemplate>
      activeTabState={activeTabState}
      caption={t('form-template-multiselect')}
      columns={columns}
      data={allTabCollection}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={filterFormFields}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      filterStateDefault={filterStateDefault}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={formTemplatesFirstLoadingSelected}
      isOpen={isOpen}
      loading={shouldRunAllQuery ? formTemplatesLoading : false}
      loadingSelected={formTemplatesLoadingSelected || isFetchingAllIds}
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onResetFilter={clearFilter}
      onSelect={onSelect}
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t('form-templates-multiselect-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedFormTemplates}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
    />
  );
}

export default memo(FormTemplatesModalTableMultiselect);
